// Unified tool registry. Every capability the assistant has — client-side
// actions and curated MCP calls — is a ToolSpec with a fully-specified schema
// and an executor. This is the foundation for constrained decoding (P1),
// the deterministic fast-path (P2), and result compaction (P4).

import { getDeals } from 'src/api';
import type { Deal } from 'src/types';

import { searchItems, searchResultToMakeItem, type MakeItem } from '../makeApi';
import { cheapestFirst, normalizePriceToNumber } from '../priceUtils';
import { mcpCallTool } from './mcpClient';

/** One retailer's matched product + price in a comparison. */
export interface PriceQuote {
  vendor: string;
  item?: MakeItem; // best match found (for the thumbnail, title, and Add button)
  priceNumber: number; // normalized; 0 when no priced match
  priceLabel: string; // display label, e.g. "$2.47" / "not found"
  cheapest?: boolean;
  note?: string; // condition flag, e.g. "Renewed" / "Used" — match transparency
  inCart?: boolean; // the user's current cart line (baseline), not a retailer quote
}

export interface PriceComparison {
  query: string;
  quotes: PriceQuote[];
  caveat?: string; // shown when matches look divergent (different model / used)
}

/** One cart line priced against the cheapest match elsewhere. */
export interface CartSavingsRow {
  item: MakeItem;
  cartLabel: string;
  best?: PriceQuote; // cheapest elsewhere, only when strictly cheaper than the cart
  savings: number; // cart price − best price, 0 when not cheaper
}

export interface CartSavings {
  rows: CartSavingsRow[];
  totalSavings: number;
}

export interface ToolContext {
  getLastSearch: () => MakeItem[];
  setLastSearch: (items: MakeItem[]) => void;
  appendResults: (items: MakeItem[]) => void;
  appendDeals: (deals: Deal[]) => void;
  appendComparison: (comparison: PriceComparison) => void;
  appendCartSavings: (savings: CartSavings) => void;
  getCartVendor?: () => string | undefined;
  pageSearch: (
    vendor: string,
    keywords: string,
    region?: string,
    zip?: string,
  ) => Promise<MakeItem[]>;
  addItems: (items: MakeItem[]) => void;
  setPendingConfirm: (msg: string) => void;
  // Cart API — wired in P3 (macro tools). Optional so P0 specs don't depend on it.
  getCartItems?: () => MakeItem[];
  removeCartIndexes?: (indexes: number[]) => void;
  setCartQuantity?: (index: number, qty: number) => void;
  clearCart?: () => void;
  saveCart?: () => void;
}

export interface ToolSpec {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>, ctx: ToolContext) => Promise<string>;
}

export const numbersFrom = (v: unknown): number[] =>
  (String(v ?? '').match(/\d+/g) || []).map(Number).filter((n) => n > 0);

export const intFrom = (v: unknown): number | undefined => {
  const m = String(v ?? '').match(/-?\d+/);
  return m ? Number(m[0]) : undefined;
};

export const priceText = (it: MakeItem): string => {
  const sym = it.priceccyS || it.ccyS || '$';
  return it.price != null && String(it.price) !== '' ? `${sym}${it.price}` : 'price n/a';
};

const obj = (props: Record<string, unknown>, required: string[] = []) => ({
  type: 'object',
  properties: props,
  required,
});

const tokenize = (s: string): string[] =>
  (s.toLowerCase().match(/[a-z0-9]+/g) || []).filter((w) => w.length > 2);

const ACCESSORY_RE =
  /\b(case|cover|charger|charging|cable|adapter|protector|skin|sticker|mount|stand|strap|band|holder|sleeve|pouch|replacement|tips|grips?|decal)\b/i;

// Scores how well a candidate title matches the query: precision (title is
// mostly query terms) + coverage (query terms hit), then de-prioritises
// accessories and non-new condition unless the query explicitly asked for them.
function matchScore(query: string, title: string): number {
  const q = tokenize(query);
  const qs = new Set(q);
  const t = tokenize(title);
  if (!t.length || !q.length) return 0;
  const overlap = t.reduce((n, w) => n + (qs.has(w) ? 1 : 0), 0);
  let s = overlap / t.length + (overlap / q.length) * 0.6;
  if (ACCESSORY_RE.test(title) && !ACCESSORY_RE.test(query)) s -= 0.6;
  if (CONDITION_RE.test(title) && !CONDITION_RE.test(query)) s -= 0.25;
  return s;
}

// Picks the priced result that best matches the query — cross-retailer search
// returns different SKUs, so we score by title rather than trusting the first
// hit (often an accessory, renewed unit, or unrelated item).
function bestMatch(query: string, items: MakeItem[]): MakeItem | undefined {
  const priced = items.filter((i) => normalizePriceToNumber(i.price, 'us') > 0);
  if (priced.length <= 1) return priced[0];
  let best = priced[0];
  let bestScore = -Infinity;
  for (const it of priced) {
    const s = matchScore(query, it.title || '');
    if (s > bestScore) {
      bestScore = s;
      best = it;
    }
  }
  return best;
}

// Extracts {vendor, id} from a pasted product URL for the supported retailers.
function parseProductUrl(raw: string): { vendor: string; id: string } | undefined {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  const host = url.hostname.replace(/^www\./, '');
  const path = url.pathname;
  if (host.includes('amazon.')) {
    const m = path.match(/\/(?:dp|gp\/product|gp\/aw\/d)\/([A-Z0-9]{10})/i);
    if (m) return { vendor: 'amazon', id: m[1].toUpperCase() };
  }
  if (host.includes('walmart.')) {
    const m = path.match(/\/ip\/(?:.*\/)?(\d+)/);
    if (m) return { vendor: 'walmart', id: m[1] };
  }
  if (host.includes('target.')) {
    const m = path.match(/\/A-(\d+)/);
    if (m) return { vendor: 'target', id: m[1] };
  }
  if (host.includes('bestbuy.')) {
    const sku = url.searchParams.get('skuId') ?? path.match(/\/(\d+)\.p/)?.[1];
    if (sku) return { vendor: 'bestbuy', id: sku };
  }
  return undefined;
}

// Match transparency: flag a matched product whose condition isn't "new", so a
// low price (e.g. a renewed unit) isn't mistaken for a like-for-like deal.
const CONDITION_RE = /\b(renewed|refurbished|refurb|pre-?owned|open[- ]?box|used)\b/i;
function conditionNote(title?: string): string | undefined {
  const w = title?.match(CONDITION_RE)?.[1]?.toLowerCase();
  if (!w) return undefined;
  if (w.startsWith('refurb')) return 'Refurbished';
  if (w.startsWith('open')) return 'Open box';
  if (w === 'renewed') return 'Renewed';
  return 'Used';
}

function jaccard(a: Set<string>, b: Set<string>): number {
  let inter = 0;
  a.forEach((x) => {
    if (b.has(x)) inter += 1;
  });
  const uni = a.size + b.size - inter;
  return uni ? inter / uni : 0;
}

// True when the matched products differ enough that comparing their prices may
// be apples-to-oranges (cross-retailer search returns different SKUs).
function titlesDiverge(quotes: PriceQuote[]): boolean {
  const sets = quotes
    .filter((q) => q.item?.title)
    .map((q) => new Set(tokenize(q.item?.title as string)));
  for (let i = 0; i < sets.length; i += 1) {
    for (let j = i + 1; j < sets.length; j += 1) {
      if (jaccard(sets[i], sets[j]) < 0.34) return true;
    }
  }
  return false;
}

// Searches each vendor in parallel and returns one best-match quote per vendor.
async function quoteVendors(query: string, vendors: string[]): Promise<PriceQuote[]> {
  return Promise.all(
    vendors.map(async (vendor): Promise<PriceQuote> => {
      try {
        const { items, redirectedTo } = await searchItems(vendor, query);
        const match = redirectedTo
          ? undefined
          : bestMatch(query, items.map(searchResultToMakeItem));
        return {
          vendor,
          item: match,
          priceNumber: match ? normalizePriceToNumber(match.price, 'us') : 0,
          priceLabel: match ? priceText(match) : 'not found',
          note: conditionNote(match?.title),
        };
      } catch {
        return { vendor, priceNumber: 0, priceLabel: 'lookup failed' };
      }
    }),
  );
}

// Flags the cheapest priced quote(s) in place; returns the cheapest.
function markCheapest(quotes: PriceQuote[]): PriceQuote | undefined {
  const priced = quotes.filter((q) => q.priceNumber > 0);
  if (!priced.length) return undefined;
  const min = Math.min(...priced.map((q) => q.priceNumber));
  quotes.forEach((q) => {
    q.cheapest = q.priceNumber > 0 && q.priceNumber === min;
  });
  return quotes.find((q) => q.cheapest);
}

// A transparency note for the whole comparison, when the cheapest looks like a
// different/used item rather than a true better price.
function comparisonCaveat(quotes: PriceQuote[], cheapest?: PriceQuote): string | undefined {
  if (cheapest?.note) {
    return `Cheapest match is ${cheapest.note.toLowerCase()} — confirm it's what you want.`;
  }
  if (titlesDiverge(quotes)) {
    return 'These matches may be different models — check each before buying.';
  }
  return undefined;
}

const COMPARE_VENDORS = 'amazon,walmart,target';

function parseVendors(raw: unknown, fallback = COMPARE_VENDORS): string[] {
  return String(raw ?? fallback)
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, 4);
}

// Compacts a (possibly large/JSON) MCP result so it fits the small model's
// context: slices the first array field to a few entries and caps length.
export function compactText(raw: string, maxChars = 1200): string {
  const t = raw.trim();
  try {
    const v = JSON.parse(t);
    if (Array.isArray(v)) {
      const head = JSON.stringify(v.slice(0, 5));
      return v.length > 5 ? `${head} …(+${v.length - 5} more)` : head;
    }
    if (v && typeof v === 'object') {
      const rec = v as Record<string, unknown>;
      for (const k of Object.keys(rec)) {
        if (Array.isArray(rec[k])) {
          const arr = rec[k] as unknown[];
          rec[k] = arr.slice(0, 5);
          const out = JSON.stringify(rec);
          const more = arr.length > 5 ? ` …(+${arr.length - 5} more)` : '';
          return (out.length > maxChars ? `${out.slice(0, maxChars)}…` : out) + more;
        }
      }
    }
    const s = JSON.stringify(v);
    return s.length > maxChars ? `${s.slice(0, maxChars)}…` : s;
  } catch {
    return t.length > maxChars ? `${t.slice(0, maxChars)}\n…(truncated)` : t;
  }
}

const searchProducts: ToolSpec = {
  name: 'search_products',
  description:
    'Search a retailer for products. Results are shown to the user as numbered cards with Add buttons.',
  inputSchema: obj(
    {
      vendor: { type: 'string', description: 'Retailer id, e.g. amazon, walmart, target.' },
      keywords: { type: 'string', description: 'What to search for.' },
      max_price: { type: 'number', description: 'Optional budget cap — only show items at or under this price.' },
    },
    ['vendor', 'keywords'],
  ),
  async execute(args, ctx) {
    const vendor = String(args.vendor ?? args.retailer ?? 'amazon');
    const keywords = String(args.keywords ?? args.query ?? args.q ?? '');
    if (!keywords) return 'Ask the user what to search for.';
    const maxPrice = intFrom(args.max_price ?? args.maxPrice ?? args.under ?? args.budget);
    let results = await ctx.pageSearch(vendor, keywords);
    if (maxPrice != null) {
      results = results.filter((r) => {
        const p = normalizePriceToNumber(r.price, 'us');
        return p > 0 && p <= maxPrice;
      });
    }
    ctx.setLastSearch(results);
    if (!results.length) {
      return maxPrice != null
        ? `No items under $${maxPrice} matched — suggest a higher budget or different search.`
        : 'No items matched — suggest a different search.';
    }
    ctx.appendResults(results);
    const data = results.map((it, i) => `${i + 1}. ${it.title || it.asin} — ${priceText(it)}`).join('\n');
    return (
      `${results.length} results now appear to the user as numbered cards in the chat with Add ` +
      'buttons — do NOT relist them. For your reference only:\n' +
      data +
      '\n\nBriefly ask which to add. Use add_to_cart(numbers="N"), or compare_price(number=N).'
    );
  },
};

const addToCart: ToolSpec = {
  name: 'add_to_cart',
  description: 'Add items from the most recent search to the cart, by their list numbers.',
  inputSchema: obj(
    {
      numbers: { type: 'string', description: 'Comma-separated item numbers, e.g. "1,3".' },
      quantity: { type: 'integer', description: 'How many of each to add (default 1).' },
    },
    ['numbers'],
  ),
  async execute(args, ctx) {
    const picked = numbersFrom(args.numbers ?? args.number ?? args.index)
      .map((n) => ctx.getLastSearch()[n - 1])
      .filter(Boolean);
    if (!picked.length) {
      return 'No matching items for those numbers — ask the user to pick numbers from the list.';
    }
    const qty = Math.max(1, Math.min(99, intFrom(args.quantity ?? args.qty) ?? 1));
    // addItems merges duplicates (+1 each), so N copies → quantity N.
    ctx.addItems(qty > 1 ? picked.flatMap((p) => Array(qty).fill(p)) : picked);
    const names = picked.map((p) => p.title || p.asin);
    const qtyNote = qty > 1 ? ` (×${qty})` : '';
    ctx.setPendingConfirm(
      names.length === 1
        ? `Added “${names[0]}”${qtyNote} to your cart.`
        : `Added ${names.length} items${qtyNote} to your cart.`,
    );
    return `Added ${picked.length} item(s)${qtyNote} to the cart: ${names.join('; ')}.`;
  },
};

const addByUrl: ToolSpec = {
  name: 'add_by_url',
  description:
    'Add a product to the cart from a pasted product page URL (Amazon, Walmart, Target, Best Buy).',
  inputSchema: obj({ url: { type: 'string', description: 'The product page URL.' } }, ['url']),
  async execute(args, ctx) {
    const raw = String(args.url ?? '').trim();
    const parsed = parseProductUrl(raw);
    if (!parsed) {
      return "I couldn't read that link — paste a product page URL from Amazon, Walmart, Target, or Best Buy.";
    }
    const { items } = await searchItems(parsed.vendor, parsed.id);
    const mapped = items.map(searchResultToMakeItem).filter((i) => i.asin);
    const match =
      mapped.find((i) => i.asin === parsed.id || i.sku === parsed.id) || mapped[0];
    if (!match) {
      return `I read the ${parsed.vendor} link but couldn't load that product — try searching for it by name.`;
    }
    ctx.addItems([match]);
    ctx.setPendingConfirm(`Added “${match.title || match.asin}” to your cart.`);
    return `Added ${match.title || match.asin} (${parsed.vendor}) to the cart.`;
  },
};

const comparePrice: ToolSpec = {
  name: 'compare_price',
  description:
    'Compare a product\'s price across retailers. Provide either keywords (e.g. "airpods pro") ' +
    'or number (an item from the last search). Optional vendors is comma-separated; defaults to ' +
    'amazon, walmart, target. Results render as a comparison the user can see and add from.',
  inputSchema: obj({
    keywords: { type: 'string', description: 'Product to compare, e.g. "airpods pro".' },
    number: {
      type: 'integer',
      description: 'Item number from the last search to compare (instead of keywords).',
    },
    vendors: { type: 'string', description: 'Comma-separated retailers, e.g. "amazon,walmart,target".' },
  }),
  async execute(args, ctx) {
    // Resolve the product to compare, in priority order:
    //   1. explicit keywords
    //   2. a last-search item by number
    //   3. context — the last item added to the cart, else the top search result
    // so "compare between walmart and amazon" compares whatever they're working on.
    let query = String(args.keywords ?? args.query ?? args.q ?? '').trim();
    if (!query) {
      const n = numbersFrom(args.number ?? args.numbers ?? args.index)[0];
      const cart = ctx.getCartItems?.() ?? [];
      const search = ctx.getLastSearch();
      const item = (n ? search[n - 1] : undefined) ?? cart[cart.length - 1] ?? search[0];
      if (item) query = item.title || item.asin;
    }
    if (!query) {
      return "Tell me which product to compare — search for it or add it to the cart first, then I'll price-check it.";
    }

    const vendors = parseVendors(args.vendors ?? args.retailers);
    const quotes = await quoteVendors(query, vendors);
    const cheapest = markCheapest(quotes);
    const caveat = comparisonCaveat(quotes, cheapest);

    ctx.appendComparison({ query, quotes, caveat });
    ctx.setPendingConfirm(
      cheapest
        ? `Cheapest is ${cheapest.vendor} at ${cheapest.priceLabel}.`
        : `I couldn't find priced matches for "${query}".`,
    );

    const summary = quotes.map((q) => `${q.vendor}: ${q.priceLabel}`).join('\n');
    return (
      `Price comparison for "${query}" now appears to the user as cards — do NOT relist them. ` +
      `For your reference only:\n${summary}\n\n` +
      (cheapest
        ? `Cheapest is ${cheapest.vendor} at ${cheapest.priceLabel}.${caveat ? ` (${caveat})` : ''} Tell the user concisely.`
        : 'No priced matches found — tell the user and suggest different terms.')
    );
  },
};

const compareCartItem: ToolSpec = {
  name: 'compare_cart_item',
  description:
    'Check whether an item already in the cart is cheaper at other retailers. number is the ' +
    "cart position (see view_cart); defaults to the first item. Shows the cart's price next to " +
    'each retailer so the user can see if they should buy elsewhere.',
  inputSchema: obj({
    number: { type: 'integer', description: 'Cart position to price-check (see view_cart).' },
    vendors: { type: 'string', description: 'Comma-separated retailers to check.' },
  }),
  async execute(args, ctx) {
    const cart = ctx.getCartItems?.() ?? [];
    if (!cart.length) return 'The cart is empty — add an item before price-checking it.';
    const n = numbersFrom(args.number ?? args.numbers ?? args.index)[0] ?? 1;
    const item = cart[n - 1];
    if (!item) return `There's no item ${n} in the cart — check view_cart for positions.`;

    const query = item.title || item.asin;
    const baseline: PriceQuote = {
      vendor: 'your cart',
      item,
      priceNumber: normalizePriceToNumber(item.price, 'us'),
      priceLabel: priceText(item),
      inCart: true,
    };

    const quotes = [baseline, ...(await quoteVendors(query, parseVendors(args.vendors)))];
    const cheapest = markCheapest(quotes);
    const caveat = comparisonCaveat(quotes, cheapest);

    ctx.appendComparison({ query, quotes, caveat });

    const confirm = !cheapest
      ? `I couldn't price-check "${query}" elsewhere right now.`
      : cheapest.inCart
        ? `Your cart already has the best price (${baseline.priceLabel}).`
        : `Cheaper at ${cheapest.vendor}: ${cheapest.priceLabel} vs ${baseline.priceLabel} in your cart.`;
    ctx.setPendingConfirm(confirm);

    const summary = quotes.map((q) => `${q.vendor}: ${q.priceLabel}`).join('\n');
    return (
      `Price check for cart item "${query}" now appears to the user as cards — do NOT relist them. ` +
      `For your reference only:\n${summary}\n\n${confirm}${caveat ? ` (${caveat})` : ''} Tell the user concisely.`
    );
  },
};

const findDeals: ToolSpec = {
  name: 'find_deals',
  description: 'List current Share-A-Cart deals, optionally for one retailer.',
  inputSchema: obj({
    retailer: { type: 'string', description: 'Optional retailer id to filter by, e.g. walmart.' },
    limit: { type: 'integer', description: 'Max deals to return (default 8).' },
  }),
  async execute(args, ctx) {
    let retailer = String(args.retailer ?? '').toLowerCase().trim();
    // "deals for my cart" → the retailer the cart is being built for.
    if (retailer === 'cart' || retailer === 'mine') {
      retailer = (ctx.getCartVendor?.() ?? '').toLowerCase();
    }
    const limit = Math.max(1, intFrom(args.limit) ?? 8);
    const now = Date.now();

    // Use the structured deals feed (same source as the /deals page), not the
    // MCP table text — so we can render real cards and drop expired offers.
    let deals = (await getDeals()).filter(
      (d) => !d.expires_at || new Date(d.expires_at).getTime() > now,
    );
    if (retailer) {
      deals = deals.filter(
        (d) =>
          d.retailer.toLowerCase() === retailer ||
          (d.retailerDisplayName ?? '').toLowerCase().includes(retailer),
      );
    }
    deals = deals.slice(0, limit);

    if (!deals.length) {
      return retailer
        ? `No current deals for ${retailer} — tell the user and suggest other retailers.`
        : 'No current deals right now — tell the user.';
    }

    ctx.appendDeals(deals);
    ctx.setPendingConfirm(
      `Here ${deals.length === 1 ? 'is a deal' : `are ${deals.length} deals`}${
        retailer ? ` for ${deals[0].retailerDisplayName ?? retailer}` : ''
      } — tap “Get offer” on any card.`,
    );
    const summary = deals
      .map((d) => `${d.retailerDisplayName ?? d.retailer}: ${d.name}${d.code ? ` (code ${d.code})` : ''}`)
      .join('\n');
    return (
      `${deals.length} deal(s) now appear to the user as cards with Get-offer links — do NOT relist ` +
      `them. For your reference only:\n${summary}\n\nBriefly tell the user what's available.`
    );
  },
};

const listRetailers: ToolSpec = {
  name: 'list_retailers',
  description: 'List the retailers Share-A-Cart supports and which can be searched.',
  inputSchema: obj({}),
  execute: async () => compactText(await mcpCallTool('sac_vendor_list', {}), 900),
};

const productDetails: ToolSpec = {
  name: 'product_details',
  description: 'Get details for a specific product by retailer and item id (ASIN/SKU).',
  inputSchema: obj(
    {
      vendor: { type: 'string', description: 'Retailer id, e.g. amazon.' },
      itemid: { type: 'string', description: 'Product id such as an Amazon ASIN.' },
    },
    ['vendor', 'itemid'],
  ),
  execute: async (args) =>
    compactText(await mcpCallTool('sac_get_product', { vendor: args.vendor, itemid: args.itemid }), 800),
};

const addCheapest: ToolSpec = {
  name: 'add_cheapest',
  description: 'Search a retailer and add the cheapest matching item(s) to the cart.',
  inputSchema: obj(
    {
      vendor: { type: 'string', description: 'Retailer id, e.g. amazon.' },
      keywords: { type: 'string', description: 'What to search for.' },
      count: { type: 'integer', description: 'How many of the cheapest to add (default 1).' },
    },
    ['vendor', 'keywords'],
  ),
  async execute(args, ctx) {
    const vendor = String(args.vendor ?? 'amazon');
    const keywords = String(args.keywords ?? args.query ?? '');
    if (!keywords) return 'Ask the user what to search for.';
    const count = Math.max(1, intFrom(args.count) ?? 1);
    const results = await ctx.pageSearch(vendor, keywords);
    ctx.setLastSearch(results);
    if (!results.length) return 'No items matched — suggest a different search.';
    ctx.appendResults(results);
    const cheapest = cheapestFirst(results).slice(0, count);
    if (!cheapest.length) return 'None of the results had a price to compare.';
    ctx.addItems(cheapest);
    const names = cheapest.map((c) => `${c.title || c.asin} (${priceText(c)})`);
    ctx.setPendingConfirm(`Added the ${cheapest.length === 1 ? 'cheapest' : `${cheapest.length} cheapest`} to your cart.`);
    return `Added: ${names.join('; ')}.`;
  },
};

const compareCart: ToolSpec = {
  name: 'compare_cart',
  description:
    'Price-check the WHOLE cart at once: finds the cheapest match elsewhere for each item and ' +
    'reports the total potential savings. Use for "is my cart cheaper elsewhere / compare my cart".',
  inputSchema: obj({
    vendors: { type: 'string', description: 'Comma-separated retailers to check.' },
  }),
  async execute(args, ctx) {
    const cart = (ctx.getCartItems?.() ?? []).slice(0, 5); // bound the fan-out
    if (!cart.length) return 'The cart is empty — add items before price-checking the whole cart.';
    const vendors = parseVendors(args.vendors);

    const rows: CartSavingsRow[] = await Promise.all(
      cart.map(async (item): Promise<CartSavingsRow> => {
        const cartPrice = normalizePriceToNumber(item.price, 'us');
        const quotes = await quoteVendors(item.title || item.asin, vendors);
        const cheaper = quotes
          .filter((q) => q.priceNumber > 0 && (cartPrice <= 0 || q.priceNumber < cartPrice))
          .sort((a, b) => a.priceNumber - b.priceNumber)[0];
        return {
          item,
          cartLabel: priceText(item),
          best: cheaper,
          savings: cheaper && cartPrice > 0 ? Math.max(0, cartPrice - cheaper.priceNumber) : 0,
        };
      }),
    );

    const totalSavings = rows.reduce((sum, r) => sum + r.savings, 0);
    ctx.appendCartSavings({ rows, totalSavings });

    const cheaperCount = rows.filter((r) => r.savings > 0).length;
    const confirm =
      cheaperCount > 0
        ? `You could save about $${totalSavings.toFixed(2)} by buying ${cheaperCount} item(s) elsewhere.`
        : 'Your cart already has the best prices I could find.';
    ctx.setPendingConfirm(confirm);
    return `${confirm} (Shown to the user as cards — do NOT relist them; just say it concisely.)`;
  },
};

const viewCart: ToolSpec = {
  name: 'view_cart',
  description: 'Show what is currently in the cart (numbered).',
  inputSchema: obj({}),
  async execute(_args, ctx) {
    const items = ctx.getCartItems?.() ?? [];
    if (!items.length) return 'The cart is empty.';
    return (
      'Current cart:\n' +
      items.map((it, i) => `${i + 1}. ${it.title || it.asin} ×${it.quantity} — ${priceText(it)}`).join('\n')
    );
  },
};

const removeFromCart: ToolSpec = {
  name: 'remove_from_cart',
  description: 'Remove items from the cart by their position number (see view_cart).',
  inputSchema: obj(
    { numbers: { type: 'string', description: 'Comma-separated cart positions, e.g. "2".' } },
    ['numbers'],
  ),
  async execute(args, ctx) {
    if (!ctx.removeCartIndexes) return 'Cart editing is unavailable.';
    const idxs = numbersFrom(args.numbers ?? args.number ?? args.index).map((n) => n - 1);
    if (!idxs.length) return 'Tell me which cart position(s) to remove.';
    ctx.removeCartIndexes(idxs);
    ctx.setPendingConfirm(`Removed ${idxs.length} item(s) from your cart.`);
    return `Removed ${idxs.length} item(s) from the cart.`;
  },
};

const setQuantity: ToolSpec = {
  name: 'set_quantity',
  description: 'Set the quantity of a cart item by its position number.',
  inputSchema: obj(
    {
      number: { type: 'integer', description: 'Cart position (see view_cart).' },
      quantity: { type: 'integer', description: 'New quantity (1-999).' },
    },
    ['number', 'quantity'],
  ),
  async execute(args, ctx) {
    if (!ctx.setCartQuantity) return 'Cart editing is unavailable.';
    const n = intFrom(args.number);
    const q = intFrom(args.quantity);
    if (!n || q == null) return 'Tell me the item number and the new quantity.';
    ctx.setCartQuantity(n - 1, q);
    ctx.setPendingConfirm(`Updated item ${n} to quantity ${q}.`);
    return `Set item ${n} quantity to ${q}.`;
  },
};

const clearCart: ToolSpec = {
  name: 'clear_cart',
  description: 'Remove everything from the cart.',
  inputSchema: obj({}),
  async execute(_args, ctx) {
    ctx.clearCart?.();
    ctx.setPendingConfirm('Cleared your cart.');
    return 'Cleared the cart.';
  },
};

const saveCart: ToolSpec = {
  name: 'save_cart',
  description: 'Save the current cart and get a shareable link.',
  inputSchema: obj({}),
  async execute(_args, ctx) {
    if (!ctx.getCartItems?.().length) return 'The cart is empty — add items before saving.';
    ctx.saveCart?.();
    ctx.setPendingConfirm('Saving your cart — the shareable link will pop up.');
    return 'Saving the cart; a shareable link will appear for the user.';
  },
};

// Built once; the registry is static (curated subset — win #6).
export function buildRegistry(): ToolSpec[] {
  return [
    searchProducts,
    addToCart,
    addCheapest,
    addByUrl,
    comparePrice,
    compareCartItem,
    compareCart,
    viewCart,
    removeFromCart,
    setQuantity,
    clearCart,
    saveCart,
    findDeals,
    listRetailers,
    productDetails,
  ];
}
